import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Images, X, CheckSquare, Square, ChatCircle,
  PaperPlaneRight, CaretLeft, CaretRight, ArrowsClockwise,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Dialog, DialogContent } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * D.FIL.6 — Galerie photos chantier (web, archi only V1)
 *
 * Vue consommée par l'archi pour préparer le CRC :
 *   - Grille de vignettes (6 col desktop / 3 col mobile)
 *   - Filtre Toutes / Incluses CRC / Non incluses
 *   - Modal détail : photo grand format + commentaires archi + toggle CRC
 *
 * Endpoints :
 *   GET    /api/chantiers/{cid}/photos?limit=50
 *   POST   /api/photos/{photo_id}/commentaire        body: { content }
 *   PUT    /api/photos/{photo_id}/inclus_dans_crc    body: { inclus_dans_crc }
 *
 * Stratégie B : si backend pas prêt, mock vide (toast erreur silencieux).
 */

const FILTERS = [
  { key: 'all', label: 'Toutes' },
  { key: 'crc', label: 'Incluses dans le CR' },
  { key: 'no_crc', label: 'Non incluses' },
];

function resolveMediaUrl(mediaUrl) {
  if (!mediaUrl) return '';
  if (mediaUrl.startsWith('http')) return mediaUrl;
  return `${API_URL}${mediaUrl}`;
}

function fmtDateTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function ProjectGallery({ chantierId }) {
  const { user } = useAuth();
  const isSupervisor = ['admin', 'architect', 'collaborateur', 'amo', 'intervenant'].includes(user?.role);

  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [activeIdx, setActiveIdx] = useState(null); // index dans `filtered`

  const loadPhotos = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/chantiers/${chantierId}/photos?limit=200`,
        { withCredentials: true },
      );
      setPhotos(Array.isArray(data) ? data : (data?.items || []));
    } catch (err) {
      // Stratégie B : si backend pas encore prêt → liste vide silencieuse
      console.warn('[ProjectGallery] photos endpoint not ready:', err?.message);
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  }, [chantierId]);

  useEffect(() => { loadPhotos(); }, [loadPhotos]);

  const filtered = useMemo(() => {
    if (filter === 'crc') return photos.filter((p) => p.inclus_dans_crc);
    if (filter === 'no_crc') return photos.filter((p) => !p.inclus_dans_crc);
    return photos;
  }, [photos, filter]);

  const activePhoto = activeIdx != null ? filtered[activeIdx] : null;

  const closeDetail = () => setActiveIdx(null);
  const prevPhoto = () => setActiveIdx((i) => (i > 0 ? i - 1 : i));
  const nextPhoto = () => setActiveIdx((i) => (i < filtered.length - 1 ? i + 1 : i));

  const toggleCrc = async (photo) => {
    if (!photo) return;
    const newVal = !photo.inclus_dans_crc;
    try {
      await axios.put(
        `${API_URL}/api/photos/${photo._id}/inclus_dans_crc`,
        { inclus_dans_crc: newVal },
        { withCredentials: true },
      );
      // Mise à jour locale optimiste
      setPhotos((prev) => prev.map((p) => (p._id === photo._id ? { ...p, inclus_dans_crc: newVal } : p)));
      toast.success(newVal ? 'Ajoutée au compte rendu' : 'Retirée du compte rendu');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    }
  };

  const addCommentaire = async (photo, content) => {
    if (!photo || !content.trim()) return null;
    try {
      const { data } = await axios.post(
        `${API_URL}/api/photos/${photo._id}/commentaire`,
        { content: content.trim() },
        { withCredentials: true },
      );
      // Le backend retourne la photo complète avec le nouveau commentaire
      const updated = data && data._id ? data : null;
      if (updated) {
        setPhotos((prev) => prev.map((p) => (p._id === photo._id ? updated : p)));
      } else {
        // Fallback : reload
        loadPhotos();
      }
      toast.success('Commentaire ajouté');
      return updated;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
      return null;
    }
  };

  return (
    <div className="space-y-3" data-testid="project-gallery">
      {/* Header */}
      <div className="bg-white border border-[#E0E0DB] p-3 flex flex-wrap items-center justify-between gap-3" data-testid="project-gallery-header">
        <div className="flex items-center gap-2">
          <Images size={20} className="text-[#C4773B]" weight="fill" />
          <h2 className="text-lg font-semibold text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Galerie photos
          </h2>
          <span className="text-xs text-[#9A9A9A]">
            {filtered.length} / {photos.length} photo{photos.length > 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-[#F8F7F4] border border-[#E0E0DB]">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                  filter === f.key
                    ? 'bg-[#1E3A5F] text-white'
                    : 'text-[#6B6B6B] hover:text-[#2C2C2C]'
                }`}
                data-testid={`project-gallery-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-none"
            onClick={loadPhotos}
            data-testid="project-gallery-reload"
          >
            <ArrowsClockwise size={14} />
          </Button>
        </div>
      </div>

      {/* Grid */}
      <div className="bg-white border border-[#E0E0DB] p-3">
        {loading ? (
          <div className="text-center py-16 text-[#9A9A9A] text-sm">Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-[#9A9A9A] text-sm" data-testid="project-gallery-empty">
            <Images size={36} className="mx-auto text-[#E0E0DB] mb-2" />
            Aucune photo {filter === 'crc' ? 'incluse dans le compte rendu' : filter === 'no_crc' ? 'hors compte rendu' : 'pour ce chantier'}.
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
            {filtered.map((p, idx) => {
              const url = resolveMediaUrl(p.media_url);
              return (
                <button
                  key={p._id}
                  onClick={() => setActiveIdx(idx)}
                  className="group relative aspect-square overflow-hidden border border-[#E0E0DB] hover:border-[#C4773B] transition-colors"
                  data-testid={`project-gallery-thumb-${p._id}`}
                >
                  <img
                    src={url}
                    alt={p.media_name || 'Photo'}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
                  />
                  {p.inclus_dans_crc && (
                    <div
                      className="absolute top-1 right-1 bg-[#16A34A] text-white p-1 rounded"
                      title="Incluse dans le compte rendu"
                    >
                      <CheckSquare size={12} weight="fill" />
                    </div>
                  )}
                  {Array.isArray(p.commentaires_archi) && p.commentaires_archi.length > 0 && (
                    <div
                      className="absolute bottom-1 left-1 bg-[#1E3A5F] text-white px-1.5 py-0.5 rounded text-[10px] flex items-center gap-0.5"
                      title={`${p.commentaires_archi.length} commentaire(s)`}
                    >
                      <ChatCircle size={10} weight="fill" /> {p.commentaires_archi.length}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal détail */}
      {activePhoto && (
        <PhotoDetailModal
          photo={activePhoto}
          isSupervisor={isSupervisor}
          onClose={closeDetail}
          onPrev={activeIdx > 0 ? prevPhoto : null}
          onNext={activeIdx < filtered.length - 1 ? nextPhoto : null}
          onToggleCrc={() => toggleCrc(activePhoto)}
          onAddCommentaire={(content) => addCommentaire(activePhoto, content)}
        />
      )}
    </div>
  );
}

// =============== Modal détail photo ===============
function PhotoDetailModal({ photo, isSupervisor, onClose, onPrev, onNext, onToggleCrc, onAddCommentaire }) {
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const url = resolveMediaUrl(photo.media_url);
  const commentaires = Array.isArray(photo.commentaires_archi) ? photo.commentaires_archi : [];

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;
    setSubmitting(true);
    const res = await onAddCommentaire(newComment);
    setSubmitting(false);
    if (res) setNewComment('');
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-5xl w-[95vw] max-h-[95vh] p-0 overflow-hidden flex flex-col md:flex-row"
        data-testid="photo-detail-modal"
      >
        {/* Image */}
        <div className="relative flex-1 bg-black flex items-center justify-center min-h-[300px] md:min-h-[500px]">
          <img
            src={url}
            alt={photo.media_name || 'Photo'}
            className="max-w-full max-h-full object-contain"
          />
          {/* Navigation prev/next */}
          {onPrev && (
            <button
              onClick={onPrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full"
              data-testid="photo-detail-prev"
            >
              <CaretLeft size={20} weight="bold" />
            </button>
          )}
          {onNext && (
            <button
              onClick={onNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full"
              data-testid="photo-detail-next"
            >
              <CaretRight size={20} weight="bold" />
            </button>
          )}
          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full"
            data-testid="photo-detail-close"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Sidebar info */}
        <div className="md:w-80 bg-white p-4 overflow-y-auto flex flex-col gap-3" style={{ maxHeight: '95vh' }}>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-[#9A9A9A]">Auteur</p>
            <p className="text-sm font-semibold text-[#2C2C2C]">
              {photo.author_name || 'Anonyme'}
              {photo.author_role && <span className="text-xs text-[#9A9A9A] ml-1">· {photo.author_role}</span>}
            </p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-[#9A9A9A]">Date</p>
            <p className="text-sm text-[#2C2C2C]">{fmtDateTime(photo.created_at)}</p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-[#9A9A9A]">Source</p>
            <p className="text-sm text-[#2C2C2C] capitalize">
              {photo.source === 'message' ? 'Fil chantier' : photo.source === 'element' ? 'Action' : photo.source || '—'}
            </p>
          </div>

          {/* Toggle CRC */}
          {isSupervisor && (
            <button
              onClick={onToggleCrc}
              className={`flex items-center gap-2 px-3 py-2 border-2 transition-colors ${
                photo.inclus_dans_crc
                  ? 'border-[#16A34A] bg-[#ECFDF5] text-[#166534]'
                  : 'border-[#E0E0DB] bg-white text-[#6B6B6B] hover:border-[#1E3A5F]'
              }`}
              data-testid="photo-detail-toggle-crc"
            >
              {photo.inclus_dans_crc ? <CheckSquare size={18} weight="fill" /> : <Square size={18} />}
              <span className="text-sm font-semibold">
                {photo.inclus_dans_crc ? 'Incluse dans le prochain compte rendu' : 'Inclure dans le prochain compte rendu'}
              </span>
            </button>
          )}

          {/* Commentaires */}
          <div className="flex-1 border-t border-[#E0E0DB] pt-3">
            <p className="text-xs font-bold uppercase tracking-wider text-[#9A9A9A] mb-2">
              Commentaires ({commentaires.length})
            </p>
            <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="photo-detail-commentaires">
              {commentaires.length === 0 ? (
                <p className="text-xs text-[#9A9A9A] italic">Aucun commentaire.</p>
              ) : (
                commentaires.map((c, i) => (
                  <div key={i} className="bg-[#FAFAF8] border-l-2 border-[#C4773B] p-2">
                    <div className="flex justify-between items-baseline mb-1">
                      <span className="text-xs font-semibold text-[#2C2C2C]">{c.author_name || 'Anonyme'}</span>
                      <span className="text-[10px] text-[#9A9A9A]">{fmtDateTime(c.created_at)}</span>
                    </div>
                    <p className="text-xs text-[#4B5563] whitespace-pre-wrap">{c.content}</p>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Ajouter commentaire (archi only) */}
          {isSupervisor && (
            <div className="border-t border-[#E0E0DB] pt-3">
              <Textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Ajouter un commentaire…"
                className="rounded-none text-sm min-h-[60px] mb-2"
                data-testid="photo-detail-comment-input"
              />
              <Button
                onClick={handleSubmitComment}
                disabled={submitting || !newComment.trim()}
                className="bg-[#1E3A5F] hover:bg-[#152A47] text-white rounded-none w-full"
                size="sm"
                data-testid="photo-detail-comment-submit"
              >
                <PaperPlaneRight size={14} className="mr-1" weight="bold" />
                {submitting ? 'Envoi…' : 'Envoyer'}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
